import type { ReactNode } from "react";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import styled from "styled-components";

const Header = styled.div<{ $expanded: boolean; $hasAction: boolean; $hasPreview: boolean; $actionWidth: number }>`
	position: relative;
	min-width: 0;
	border-radius: ${({ $expanded }) => $expanded ? "12px 12px 0 0" : "12px"};
	border-bottom: ${({ $expanded }) => $expanded ? "1px solid #edf0f5" : "none"};
	.toggle { display: flex; align-items: center; gap: 12px; width: 100%; min-width: 0; min-height: 72px; box-sizing: border-box; padding: 12px 16px; border: 0; border-radius: inherit; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
	.toggle:hover:not(:disabled) { background: #f8fafc; }
	.toggle:disabled { cursor: default; }
	.toggle:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
	.header-icon { display: flex; flex-shrink: 0; }
	.copy { min-width: 0; flex: 1; margin-right: ${({ $hasAction, $hasPreview, $actionWidth }) => $hasPreview ? "292px" : $hasAction ? `${$actionWidth}px` : "0"}; }
	.heading { display: flex; align-items: center; flex-wrap: wrap; gap: 5px 12px; font-size: 14px; font-weight: 600; line-height: 1.5; }
	.badge { font-size: 13px; font-weight: 400; padding: 2px 6px; border-radius: 4px; }
	.description { display: block; margin-top: 4px; color: #526174; font-size: 14px; line-height: 1.5; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.chevron { flex-shrink: 0; color: #98a2b3; }
	.header-action, .header-preview { position: absolute; right: 45px; top: 50%; transform: translateY(-50%); }
	.header-preview { width: 280px; height: 36px; overflow: hidden; pointer-events: none; }
	@container (max-width: 840px) {
		.header-preview { display: none; }
		.copy { margin-right: ${({ $hasAction, $actionWidth }) => $hasAction ? `${$actionWidth}px` : "0"}; }
	}
	@container (max-width: 480px) {
		.toggle { padding: 12px; gap: 8px; }
		.description { white-space: normal; overflow-wrap: anywhere; }
		.header-action { right: 37px; }
		.copy { margin-right: ${({ $hasAction, $actionWidth }) => $hasAction ? `${$actionWidth - 4}px` : "0"}; }
		${({ $hasAction, $actionWidth }) => $hasAction && $actionWidth > 44 ? `
			.copy { margin-right: 0; }
			.header-action { position: static; transform: none; padding: 0 12px 10px; display: flex; justify-content: flex-end; }
		` : ""}
	}
`;

interface IStatCardHeaderProps {
	title: string;
	label: string;
	icon: ReactNode;
	description: ReactNode;
	descriptionTitle?: string;
	descriptionColor?: string;
	badge?: { label: string; color: string; background: string };
	expanded?: boolean;
	controls?: string;
	disabled?: boolean;
	opensPopup?: boolean;
	onToggle: () => void;
	action?: ReactNode;
	actionWidth?: number;
	preview?: ReactNode;
}

export const StatCardHeader = ({ title, label, icon, description, descriptionTitle, descriptionColor, badge, expanded, controls, disabled, opensPopup, onToggle, action, actionWidth = 44, preview }: IStatCardHeaderProps) => {
	const visibleAction = expanded ? action : undefined;
	const visiblePreview = !expanded && !disabled ? preview : undefined;
	return <Header className="stat-card-header" $expanded={!!expanded} $hasAction={!!visibleAction} $hasPreview={!!visiblePreview} $actionWidth={actionWidth}>
		<button className="toggle" type="button" disabled={disabled} aria-expanded={expanded} aria-controls={controls} aria-haspopup={opensPopup ? "dialog" : undefined} aria-label={label} title={label} onClick={onToggle}>
			<span className="header-icon">{icon}</span>
			<span className="copy" role="status" aria-live="polite">
				<span className="heading">{title}{badge && <span className="badge" style={{ color: badge.color, background: badge.background }}>{badge.label}</span>}</span>
				<span className="description" title={descriptionTitle} style={{ color: descriptionColor }}>{description}</span>
			</span>
			{!disabled && (opensPopup ? <ChevronRight className="chevron" size={17} aria-hidden="true" /> : expanded ? <ChevronUp className="chevron" size={17} aria-hidden="true" /> : <ChevronDown className="chevron" size={17} aria-hidden="true" />)}
		</button>
		{visibleAction && <div className="header-action">{visibleAction}</div>}
		{visiblePreview && <div className="header-preview" aria-hidden="true">{visiblePreview}</div>}
	</Header>;
};
